<template>
  <div class="button-upload is-flex" draggable="true" @dragenter="dragEnter" @dragover="dragOver" @drop="drop" v-bind:style="projectImageBackground">
    <div v-if="isUploading" class="progress-container">
      {{ $t("projectPage.uploading") }}...
      <progress class="progress" :value="monitor.progress" max="100">{{monitor.progress}}%</progress>
    </div>
    <div class="visible-container is-flex" :class="{'no-image': image === ''}">
      <button v-if="!isUploading" class="preview-button" :class="{'small': isPin, 'no-image': image === ''}" @click="selectImage">
        <div v-if="!isDragging" class="upload">
          <span v-if="!isPin">
            <i class="fal fa-camera fa-2x"></i><br/>
          </span>
          <span class="is-size-8 has-text-centered">
            <span v-if="!isPin">{{ $t("projectPage.upload") }}</span>
            <span v-if="isPin">{{ $t("projectPage.uploadPin") }}</span>
          </span>
        </div>
        <div v-if="isDragging && !isPin" class="drop"><span class="icon"><i class="fal fa-dot-circle"></i></span><br/><span class="is-size-6">{{ $t("projectPage.drop") }}</span></div>
      </button>
      <div v-if="image && !isDragging && !isUploading && !hideImage && !isPin" class="remove button is-small" :class="{'px-1': isPin}" @click="removeImage">{{ $t("projectPage.remove") }}</div>
      <div class="help" v-if="fileError && !isDragging && !isPin">{{fileError}}</div>
    </div>
    <modal-attach :parentID="_uid" @attached="setImage" :isProfileImage="isProfileImage" :isAdminPage="isAdminPage" :showAvatarPicker="showAvatarPicker"></modal-attach>
  </div>
</template>

<script>
import dragAndDrop from '@/mixins/drag-and-drop'
import imageProxy from '@/libs/image-proxy'
import firebase from '@/libs/firebase'

import ModalAttach from '@/components/Modals/Attach'

const maxSize = 10485760 // 10MB

export default {
  components: { ModalAttach },
  mixins: [dragAndDrop],
  props: ['image', 'callback', 'proxyWidth', 'proxyHeight', 'generated', 'isAdminPage', 'isPin', 'isProfileImage', 'showAvatarPicker'],
  data: function () {
    return {
      file: '',
      fileError: '',
      hideImage: this.generated,
      fileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'],
      hasFileToUpload: false,
      monitor: {}
    }
  },
  mounted: function () {
    if (!this.image) {
      this.removeImage()
    }
  },
  computed: {
    imageURL: function () {
      if (!this.proxyWidth || !this.proxyHeight) {
        return this.image
      }

      return imageProxy.proxy(this.image, { width: this.proxyWidth, height: this.proxyHeight })
    },
    isUploading: function () {
      return this.monitor.uploading
    },
    projectImageBackground: function () {
      const style = {}
      if (!this.hideImage) {
        style.backgroundImage = `url('${this.image}')`
      }
      return style
    },
    isDevEnv: function () {
      return this.$webRoot === 'http://localhost'
    }
  },

  methods: {
    selectImage: function (event) {
      this.$store.dispatch('showAttachModal', { type: 'image', parentID: this._uid })
    },
    setImage: function (value) {
      this.hideImage = false
      if (value.file) {
        this.checkFile(value.file)
      } else {
        return this.callback(value.url, value.type)
      }
    },
    checkFile: function (fileToCheck) {
      if (fileToCheck) {
        if (fileToCheck.size > maxSize) {
          this.$parent.error = '10MB File size limit for pins please upload a smaller image'
          return this.$parent.error
        } else if (this.fileTypes.indexOf(fileToCheck.type) === -1) {
          this.fileError = 'Only images allowed'
        } else {
          this.fileError = ''
          this.file = fileToCheck
          this.attachImage()
        }
      }
    },
    attachImage: function () {
      this.hasFileToUpload = false
      const reader = new FileReader()

      const onSuccess = () => {
        this.hasFileToUpload = true
        this.callback(reader.result)
      }

      const onFail = () => {
        this.file = ''
        this.fileError = 'Unable to read image'
        this.hasFileToUpload = false
      }

      // cordova work-around; do not have addEventListener
      if (reader.addEventListener) {
        reader.addEventListener('load', onSuccess, onFail)
      } else {
        reader.onload = onSuccess
        reader.onerror = onFail
      }

      reader.readAsDataURL(this.file)
    },
    removeImage: function () {
      this.callback('')
      this.file = ''
      this.fileError = ''
      this.hasFileToUpload = false
    },
    dropHandler: function (event, data, isOverDropTarget) {
      if (data.uri) {
        if (data.image) {
          return this.callback(data.uri)
        } else {
          this.fileError = 'Only images allowed'
        }
      }

      if (data.file) {
        this.checkFile(data.file)
      }
    },
    upload: function (bucketName, key) {
      return new Promise((resolve, reject) => {
        if (!this.hasFileToUpload) {
          return resolve(this.image)
        }

        firebase.projectBoard.signIn().then(() => {
          this.monitor = { task: null, progress: 0, done: false, error: false, uploading: false }
          const bucket = firebase.storage().ref(bucketName)
          this.monitor.task = bucket.child(`${key}`).putString(this.image, 'data_url')
          this.monitorUpload(resolve, reject)
        }).catch((error) => {
          console.error(`Avatar Image:: Unable to sign in to firebase - ${error}`)
          reject(error)
        })
      })
    },
    uploadImage: function (bucketName, key, imageURL) {
      return new Promise((resolve, reject) => {
        if (!this.hasFileToUpload) {
          return resolve(this.image)
        }

        firebase.projectBoard.signIn().then(() => {
          this.monitor = { task: null, progress: 0, done: false, error: false, uploading: false }
          const bucket = firebase.storage().ref(bucketName)
          this.monitor.task = bucket.child(`${key}`).putString(imageURL, 'data_url')
          this.monitorUpload(resolve, reject)
        }).catch((error) => {
          console.error(`Avatar Image:: Unable to sign in to firebase - ${error}`)
          reject(error)
        })
      })
    },
    monitorUpload: function (resolve, reject) {
      this.monitor.task.on('state_changed', (snapshot) => {
        this.monitor.uploading = snapshot.state === firebase.storage.TaskState.RUNNING
        this.monitor.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      }, (error) => {
        this.monitor.task = null
        this.monitor.error = true
        this.monitor.uploading = false
        reject(error)
      }, async () => {
        const URL = await this.monitor.task.snapshot.ref.getDownloadURL()
        resolve(URL)
        this.monitor.task = null
        this.monitor.done = true
        this.monitor.uploading = false
        this.hasFileToUpload = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  input[type="file"] {
    display: none;
  }

  .preview-button {
    border: none;
    border-radius: 50%;
    background-color: $upload-button;
    width: 90px;
    height: 90px;
    color: white;
    &:focus,
    &:active {
      outline: 0;
    }

    .upload {
      margin-top: 1.1rem;
    }

    .far, .fal {
      font-size: 2rem;
      padding-bottom: 5px;
    }

    &:hover {
      cursor: pointer;
      background-color: $upload-button-hover;
    }
    div {
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
    &.small {
      background-color: $upload-button;
      font-weight: 500;
      color: white;
      width: 55px;
      height: 55px;
      &.no-image {
         background-color: #D8D8D8;
         color: black;
       }
      .upload {
        margin-top: 0.8rem;
      }
    }
  }

  .button-upload {
    background-size: cover;
    background-position: center;
    position: relative;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .help {
    color: $danger;
    font-size: 0.75rem;
    text-align: center;
    opacity: 1;
    @media screen and (max-width: 767px) {
      font-size: 0.6rem;
    }
  }

  .remove {
    font-size: 0.6rem;
    cursor: pointer;
    text-align: center;
    margin-top: 0.25rem;
    position: relative;
    z-index: 2;
  }

  .button-upload input[type=file] {
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    z-index: 0;
    max-width: 100%;
    height: 100%;
    display: block;
    cursor: pointer;
  }

  .progress-container {
    position: absolute;
    background: #0a0a0a;
    opacity: 0.75;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    color: white;
  }

  .visible-container {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    opacity: 0.25;
    &.no-image {
      opacity: 1;
    }
  }

  .visible-container:hover {
    opacity: 1;
  }
</style>
